import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unwrap, messageOf } from '@truecare/api-client';
import type { CaregiverStatus } from '@truecare/schemas';
import { Alert, Button, Card, CardHeader } from '@truecare/ui';
import { api } from '../api';
import { SUPPORT_EMAIL } from '../support-email';
import { CaregiverPaymentTaxSection } from './CaregiverPaymentTaxSection';

const PROFILE_KEY = ['caregiver', 'me'];
const ONBOARDING_KEY = ['caregiver', 'onboarding-readiness'];
const CONTRACT_KEY = ['caregiver', 'contract'];

/**
 * Phase 5g (change-list 1.1) — the ONBOARDING-stage caregiver workspace tab.
 *
 * An approved caregiver lands here to complete the three things that gate
 * ACTIVE: the independent-contractor agreement (click-through), SIN, and
 * banking (submitted here, operator-verified). The checklist reads the same
 * `checkOnboardingComplete` gate the backend auto-activates on, so when the
 * last item clears the caregiver flips to ACTIVE and the parent re-renders
 * into the working tabs.
 */
export function CaregiverOnboardingTab({
  status,
  hstRegistered,
}: {
  status: CaregiverStatus;
  hstRegistered: boolean;
}) {
  const readinessQuery = useQuery({
    queryKey: ONBOARDING_KEY,
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/onboarding-readiness')),
  });

  // The QueryClient runs `retry: false`, so a single transient fetch failure
  // leaves `data` undefined. Without these guards every checklist item fell back
  // to `false` — an already-complete caregiver would see every step unchecked
  // ("todo") with no error and no way to recover. Branch explicitly (mirrors
  // CaregiverSubmitPanel / CaregiverAvailabilitySection) so a loading or failed
  // fetch reads as exactly that, and offer a retry given retries are off.
  if (readinessQuery.isPending) {
    return (
      <Card padding="lg">
        <p className="font-body text-sm text-ink-muted">Checking your setup…</p>
      </Card>
    );
  }

  if (readinessQuery.isError || !readinessQuery.data) {
    return (
      <Card padding="lg">
        <Alert tone="danger" title="Couldn't load your setup checklist">
          Something went wrong loading your onboarding status. Please try again.
        </Alert>
        <div className="mt-4">
          <Button
            type="button"
            variant="secondary"
            loading={readinessQuery.isFetching}
            onClick={() => void readinessQuery.refetch()}
            data-testid="onboarding-retry"
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  const missing: { category: string; type: string; reason: string }[] =
    readinessQuery.data.missing;
  const has = (type: string) => missing.some((m) => m.type === type);
  const contractDone = !has('CONTRACT_UNACCEPTED');
  // Tax identity is done once whichever applies is on file — INDIVIDUAL clears
  // SIN_MISSING, CORPORATION clears BUSINESS_NUMBER_MISSING.
  const taxIdentityDone = !has('SIN_MISSING') && !has('BUSINESS_NUMBER_MISSING');
  const bankingVerified = !has('BANKING_UNVERIFIED');
  // Distinguish "hasn't submitted" from "submitted, awaiting operator verify"
  // and "rejected" — the checklist otherwise reads "Submit your banking" even
  // after she's submitted it.
  const { bankingSubmitted, bankingRejected, bankingRejectionReason } =
    readinessQuery.data;
  const bankingPending = bankingSubmitted && !bankingVerified && !bankingRejected;

  // Phase 5g audit F2 — the ONBOARDING → ACTIVE gate RE-CHECKS full vetting, so
  // a credential/compliance expiry (or a profile field cleared) mid-onboarding
  // can block activation even with the 3 checklist items done. Surface any such
  // items so the caregiver isn't silently stuck with everything ticked.
  const blockers = missing.filter((m) => m.category !== 'ONBOARDING');

  return (
    <div className="flex flex-col gap-6">
      <Card padding="lg" accent="left" tone="gold">
        <CardHeader
          title="You're approved — let's finish setting you up"
          description="Complete these steps so we can offer you shifts and pay you. As soon as the last one is done, your account goes active automatically."
        />
        <ul className="mt-4 flex flex-col gap-3">
          <ChecklistRow
            done={contractDone}
            label="Accept the independent-contractor agreement"
            todo="Review and accept below."
          />
          <ChecklistRow
            done={taxIdentityDone}
            label="Add your tax identity (SIN or Business Number)"
            todo="Required for year-end tax reporting — your SIN if you're a sole proprietor, or your Business Number if incorporated."
          />
          <ChecklistRow
            done={bankingVerified}
            pending={bankingPending}
            label="Submit banking (void cheque) + operator verification"
            todo={
              bankingRejected
                ? `Your banking couldn't be verified${
                    bankingRejectionReason ? `: ${bankingRejectionReason}` : ''
                  }. Please resubmit your void cheque below.`
                : 'Submit your details below; an operator verifies the void cheque before your first payout.'
            }
            pendingNote="Submitted — TrueCare is verifying your void cheque. No action needed on your part."
          />
        </ul>
        <p className="mt-4 font-body text-xs text-ink-muted">
          Availability isn't required to activate, but you won't be offered
          shifts until you add some — you can do that on the Availability tab
          any time.
        </p>
      </Card>

      {/* F2 — a vetting item lapsed during onboarding (rare, but the gate
          re-checks). Tell the caregiver why they're stuck instead of leaving
          all three items ticked with no activation. */}
      {blockers.length > 0 && (
        <Alert
          tone="warning"
          title="One more thing needs attention before you can activate"
        >
          <ul className="mt-1 list-disc pl-5">
            {blockers.map((b) => (
              <li key={b.type}>{b.reason}</li>
            ))}
          </ul>
          <p className="mt-2 text-sm">
            If this is a document that needs re-uploading, contact TrueCare™ at{' '}
            <a
              className="font-semibold text-forest underline underline-offset-2"
              href={`mailto:${SUPPORT_EMAIL}`}
            >
              {SUPPORT_EMAIL}
            </a>{' '}
            — we'll reopen it for you. Address or phone details you can fix in{' '}
            <Link
              to="/profile"
              className="font-semibold text-forest underline underline-offset-2"
            >
              your profile
            </Link>
            .
          </p>
        </Alert>
      )}

      {/* Contract — click-through acceptance (Q5=A). */}
      {!contractDone && <ContractAcceptCard />}

      {/* Banking + SIN — the forms live in the shared Payment & Tax section,
          which is now permitted during ONBOARDING (5g-c relaxed its guard). */}
      <CaregiverPaymentTaxSection
        status={status}
        hstRegistered={hstRegistered}
      />
      {/* audit H7 — hstRegistered MUST come from server state, threaded from
          me.tsx. Hardcoding `false` initialized the HstCard toggle unchecked,
          so a caregiver who had registered for HST could re-save it OFF and
          silently corrupt a T4A-relevant field. */}
    </div>
  );
}

function ChecklistRow({
  done,
  label,
  todo,
  pending = false,
  pendingNote,
}: {
  done: boolean;
  label: string;
  todo: string;
  /** Submitted / in-progress but not yet done — e.g. banking submitted, awaiting
   *  operator verification. Renders a distinct "no action needed" state so the
   *  caregiver isn't told to do something she's already done. Ignored when done. */
  pending?: boolean;
  pendingNote?: string;
}) {
  const isPending = pending && !done;
  const marker = done
    ? 'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-forest text-xs text-paper'
    : isPending
      ? 'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/20 text-xs text-gold'
      : 'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-border text-xs text-ink-muted';
  return (
    <li className="flex items-start gap-3">
      <span aria-hidden className={marker}>
        {done ? '✓' : isPending ? '◷' : ''}
      </span>
      <span className="font-body text-sm">
        <span className={done ? 'text-ink-muted line-through' : 'font-medium text-forest'}>
          {label}
        </span>
        {isPending ? (
          <span className="block text-ink-muted">{pendingNote}</span>
        ) : (
          !done && <span className="block text-ink-muted">{todo}</span>
        )}
      </span>
    </li>
  );
}

/**
 * Phase 5g (Q5=A) — click-through IC-agreement acceptance. The agreement text
 * is placeholder pending the operator's final legal copy (mirrors the
 * marketing legal pages). Accepting POSTs the current version; the backend
 * 409s a stale version, which surfaces as a reload prompt.
 */
function ContractAcceptCard() {
  const qc = useQueryClient();
  const [agreed, setAgreed] = useState(false);
  const [typedName, setTypedName] = useState('');

  const contractQuery = useQuery({
    queryKey: CONTRACT_KEY,
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/contract')),
  });

  const accept = useMutation({
    mutationFn: (body: { version: string; typedLegalName: string }) =>
      unwrap(api.POST('/api/v1/me/caregiver/contract', { body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CONTRACT_KEY });
      void qc.invalidateQueries({ queryKey: ONBOARDING_KEY });
      void qc.invalidateQueries({ queryKey: PROFILE_KEY });
    },
  });

  const data = contractQuery.data;
  const version = data?.currentVersion;
  const canSubmit =
    agreed && typedName.trim().length >= 2 && !!version && !accept.isPending;

  return (
    // Bug #5 — match the accent of every other onboarding box (checklist +
    // Payment/Tax all use accent="left" tone="gold"); this card was the lone
    // undecorated one, breaking the visual rhythm of the "Get started" tab.
    <Card padding="lg" accent="left" tone="gold">
      <CardHeader
        title={data?.title ?? 'Independent-contractor agreement'}
        description="Please review and accept to continue."
      />
      {data ? (
        <div
          className="mt-4 max-h-64 overflow-y-auto rounded-md border border-border bg-paper p-4 font-body text-sm leading-relaxed text-ink [&_h1]:mb-2 [&_h1]:text-base [&_h1]:font-semibold [&_p]:mb-3"
          // Operator-authored agreement-template HTML (Phase 23) — not user input.
          dangerouslySetInnerHTML={{ __html: data.bodyHtml }}
        />
      ) : (
        <p className="mt-4 font-body text-sm text-muted">Loading agreement…</p>
      )}

      {accept.isError && (
        <Alert tone="danger" title="Couldn't record your acceptance">
          {messageOf(accept.error, 'Please reload and try again.')}
        </Alert>
      )}

      <label className="mt-4 flex items-start gap-2 font-body text-sm">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-1"
          data-testid="contract-agree-checkbox"
        />
        <span>I have read and agree to this independent-contractor agreement.</span>
      </label>

      <label className="mt-3 block font-body text-sm text-ink">
        Type your full legal name to sign
        <input
          type="text"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          placeholder="Your full legal name"
          className="mt-1 w-full rounded-md border border-border bg-paper px-3 py-2 font-body text-sm text-ink"
          data-testid="contract-typed-name"
        />
      </label>

      <div className="mt-4">
        <Button
          type="button"
          variant="primary"
          disabled={!canSubmit}
          onClick={() =>
            version &&
            accept.mutate({ version, typedLegalName: typedName.trim() })
          }
          data-testid="contract-accept-button"
        >
          {accept.isPending ? 'Accepting…' : 'Accept agreement'}
        </Button>
      </div>
    </Card>
  );
}
